import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Play, Pencil, CalendarDays, Search } from "lucide-react";
import { api } from "../../lib/api";
import JaceLayout from "./JaceLayout";
import { JACE, TYPE_LABELS, STATUS_COLORS, mondayOf, addDays, isoDay, isoWeek, fmtWeekRange, WEEKDAY_NL, weekStatus, isReady, isDone } from "./shared";

const INTAKE_LABELS = { intake_required: "INTAKE REQUIRED", intake_completed: "AWAITING REVIEW", reviewed: "✓ REVIEWED", needs_follow_up: "FOLLOW-UP" };
const INTAKE_COLORS = { intake_required: "#D4A93A", intake_completed: "#F97316", reviewed: "#4ADE80", needs_follow_up: "#60A5FA" };

export default function JaceDashboard() {
  const nav = useNavigate();
  const [sessions, setSessions] = useState(null);
  const [ptQ, setPtQ] = useState("");
  const [ptResults, setPtResults] = useState(null);

  useEffect(() => {
    api.get("/jace/sessions").then(({ data }) => setSessions(data.items)).catch(() => setSessions([]));
  }, []);

  // PT-klanten zoeken (zelfde centrale klantprofiel, niet-gevoelige info)
  useEffect(() => {
    if (ptQ.trim().length < 2) { setPtResults(null); return; }
    const t = setTimeout(() => {
      api.get("/jace/pt-clients", { params: { q: ptQ.trim() } }).then(({ data }) => setPtResults(data.items)).catch(() => setPtResults([]));
    }, 350);
    return () => clearTimeout(t);
  }, [ptQ]);

  const today = new Date();
  const monday = mondayOf(today);
  const weekNum = isoWeek(today);
  const todayIso = isoDay(today);

  const weekSessions = (sessions || []).filter((s) => {
    if (!s.date || s.status === "ARCHIVED") return false;
    return s.date >= isoDay(monday) && s.date <= isoDay(addDays(monday, 6));
  }).sort((a, b) => (a.date + (a.start_time || "")).localeCompare(b.date + (b.start_time || "")));

  const wStatus = weekStatus(weekSessions);
  const readyCount = weekSessions.filter((s) => isReady(s)).length;
  const weekReady = weekSessions.length > 0 && weekSessions.every((s) => isReady(s));

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = addDays(monday, i);
    return { date: isoDay(d), label: WEEKDAY_NL[i], day: d.getDate(), sessions: weekSessions.filter((s) => s.date === isoDay(d)) };
  });

  return (
    <JaceLayout>
      <div data-testid="jace-dashboard">
        <div className="section-tag section-tag-dark mb-3">// THIS WEEK</div>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h2 className="font-display uppercase text-4xl md:text-6xl">Week {weekNum}.</h2>
            <div className="chip text-[#A3A3A3] mt-2" data-testid="jace-week-range">{fmtWeekRange(monday)}</div>
          </div>
          <div className="flex gap-3">
            <Link to="/jace/sessions" className="btn-ghost-dark inline-flex items-center gap-2" data-testid="jace-open-planner"><CalendarDays size={14}/> OPEN WEEK PLANNER</Link>
            <Link to="/jace/sessions/new" className="px-6 py-3.5 chip inline-flex items-center gap-2" style={{ background: JACE, color: "#0A0A0A" }} data-testid="jace-new-session-btn"><Plus size={14}/> NEW SESSION</Link>
          </div>
        </div>

        {/* Week progress */}
        <div className="border border-[#F5F3F0]/15 p-5 mb-10 flex flex-wrap items-center gap-6" data-testid="jace-week-progress">
          <div>
            <div className="chip text-[#A3A3A3]">WEEK STATUS //</div>
            {sessions === null ? (
              <div className="font-display text-3xl mt-1 text-[#4A4A4A]">…</div>
            ) : weekReady ? (
              <div className="font-display text-3xl mt-1" style={{ color: "#4ADE80" }} data-testid="jace-week-ready">WEEK READY ✓</div>
            ) : (
              <div className="font-display text-3xl mt-1" data-testid="jace-week-count">{readyCount} / {weekSessions.length} SESSIONS READY</div>
            )}
          </div>
          <div className="flex-1 min-w-40 h-1 bg-[#F5F3F0]/10">
            <div className="h-1 transition-all" style={{ width: `${weekSessions.length ? (readyCount / weekSessions.length) * 100 : 0}%`, background: JACE }} />
          </div>
          <div className="chip" style={{ color: weekReady ? "#4ADE80" : "#A3A3A3" }}>{wStatus}</div>
        </div>

        {/* PT-klanten zoeken (centraal klantprofiel, niet-gevoelige info) */}
        <div className="border border-[#F5F3F0]/15 p-5 mb-10" data-testid="jace-pt-search">
          <div className="chip text-[#A3A3A3] mb-3">// PT CLIENTS — ZOEK OP NAAM, EMAIL OF TELEFOON</div>
          <div className="flex items-center gap-3">
            <Search size={15} style={{ color: JACE }}/>
            <input value={ptQ} onChange={(e) => setPtQ(e.target.value)} placeholder="Zoek PT-klant…" className="editorial-input dark flex-1" data-testid="jace-pt-search-input" />
          </div>
          {ptResults && (
            <div className="mt-4" data-testid="jace-pt-results">
              {ptResults.length === 0 ? <div className="chip text-[#4A4A4A] py-2">Geen PT-klanten gevonden.</div> : (
                <div className="line-t line-dark">{ptResults.map((c) => (
                  <div key={c.email} className="line-b line-dark py-3 flex flex-wrap items-center gap-3" data-testid={`jace-pt-${c.email}`}>
                    <span className="font-display uppercase flex-1 min-w-32">{c.name}</span>
                    <span className="chip px-2 py-0.5 border" style={{ color: INTAKE_COLORS[c.intake_status], borderColor: `${INTAKE_COLORS[c.intake_status]}55` }}>{INTAKE_LABELS[c.intake_status]}</span>
                    {c.boxing_level && <span className="chip text-[#A3A3A3]">{c.boxing_level}</span>}
                    {c.package && <span className="chip" style={{ color: JACE }}>{c.package.remaining}/{c.package.total} SESSIES</span>}
                    {c.next_session && <span className="chip text-[#DDD6CB]">NEXT: {c.next_session.date} {c.next_session.time}</span>}
                    {c.main_goal && <span className="chip text-[#4A4A4A] truncate max-w-48">{c.main_goal}</span>}
                  </div>
                ))}</div>
              )}
            </div>
          )}
        </div>

        {/* Per dag gegroepeerd */}
        {sessions === null ? (
          <div className="chip text-[#A3A3A3]">Laden…</div>
        ) : (
          <div className="space-y-1" data-testid="jace-week-days">
            {days.map((d) => {
              const isToday = d.date === todayIso;
              return (
                <div key={d.date} className={`border ${isToday ? "border-[#C6FF3C]/40" : "border-[#F5F3F0]/10"} p-4`} data-testid={`jace-day-${d.date}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="chip" style={{ color: isToday ? JACE : "#A3A3A3" }}>{d.label} {d.day}</span>
                    {isToday && <span className="chip px-2 py-0.5" style={{ background: JACE, color: "#0A0A0A" }}>VANDAAG</span>}
                  </div>
                  {d.sessions.length === 0 ? (
                    <div className="chip text-[#4A4A4A]">No sessions</div>
                  ) : (
                    d.sessions.map((s) => (
                      <div key={s.session_id} className="flex flex-wrap items-center gap-3 md:gap-5 py-2.5 border-t border-[#F5F3F0]/10" data-testid={`jace-week-session-${s.session_id}`}>
                        <span className="chip w-14" style={{ color: JACE }}>{s.start_time || "—"}</span>
                        <span className="font-display text-xl uppercase flex-1 min-w-32 truncate">{s.title || "—"}</span>
                        <span className="chip text-[#A3A3A3] hidden md:inline w-40">{TYPE_LABELS[s.type]}</span>
                        <span className="chip text-[#A3A3A3] hidden lg:inline w-32 truncate">{s.assigned_coach ? `COACH: ${s.assigned_coach.toUpperCase()}` : ""}</span>
                        <span className="chip text-[#4A4A4A] w-16">{s.planned_duration} MIN</span>
                        <span className="chip px-2 py-0.5 border" style={{ color: STATUS_COLORS[s.status], borderColor: `${STATUS_COLORS[s.status]}55` }}>{s.status}</span>
                        <span className="flex gap-1">
                          <Link to={`/jace/sessions/${s.session_id}`} className="w-8 h-8 border border-[#F5F3F0]/20 flex items-center justify-center text-[#A3A3A3] hover:text-[#F5F3F0] chip" title="Open" data-testid={`jace-open-${s.session_id}`}>↗</Link>
                          <Link to={`/jace/sessions/${s.session_id}/edit`} className="w-8 h-8 border border-[#F5F3F0]/20 flex items-center justify-center text-[#A3A3A3] hover:text-[#F5F3F0]" title="Edit" data-testid={`jace-edit-${s.session_id}`}><Pencil size={12}/></Link>
                          {(s.status === "READY" || s.status === "IN PROGRESS") && (
                            <button onClick={() => nav(`/jace/play/${s.session_id}`)} className="w-8 h-8 flex items-center justify-center" style={{ background: JACE, color: "#0A0A0A" }} title="Training Play" data-testid={`jace-play-${s.session_id}`}><Play size={13}/></button>
                          )}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </JaceLayout>
  );
}
